import {
  Button,
  Col,
  Form,
  Grid,
  message,
  Modal,
  PageHeader,
  Row,
  Select,
  Typography,
  Space,
} from "antd";
import { Panel, PanelGroup } from "react-resizable-panels";
import { PanelResizeDivider } from "common/components/panelResizeDivider/panelResizeDivider";
import LayoutCss from "layout/layout.module.scss";
import { groupBy, map } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  bulkEditCandidateList,
  selectCandidates,
  setCandidateList,
  setShouldReinitialize,
  updateCandidateList,
} from "redux/slices/candidateSlice";
import { selectConfigration } from "redux/slices/configrationSlice";
import { selectUserId, selectUserRoles } from "redux/slices/userSlice";
import K from "utilities/constants";
import { isPermissionPresent } from "utilities/generalUtility";
import "../../../create/jobCard.scss";
import { setCandidateUdf } from "redux/slices/udfSlice";
import UDF from "redux/models/udf";
import Class from "redux/models/class";
import LookupTable from "redux/models/lookupTable";
import { DataTableLayout } from "common/components/dataTableLayout/dataTableLayout";
import { usePageFilter } from "hooks/usePageFilters";
import { SettingFilled } from "@ant-design/icons";
import { ClientSideDataGrid } from "common/components/clientSideDataGrid/clientSideDataGrid";
import AgGridUtility from "utilities/agGridUtility";
import Candidate from "redux/models/candidate";
import styles from "../../../create/clientsJob.module.scss";
import ClassSettingModal from "./classSettingsModal";
import ClassStatistics from "../../../create/classStatistics";
import AssignCandidate from "./assignCandidateClass/assignCandidateClass";
import { getColumns } from "./columns";
import ViewActivity from "../../../../candidates/viewActivity";
import { AddClassCandidateButton } from "./addClassCandidateButton/addClassCandidateButton";
import DetachCandidateButton from "./detachCandidateButton/detachCandidateButton";

const { Text } = Typography;
const { Lookup } = K.Network.URL;

export default function ClientClassDetails() {
  const history = useHistory();
  const dispatch = useDispatch();
  const screens = Grid.useBreakpoint();

  const userId = useSelector(selectUserId);
  const userRoles = useSelector(selectUserRoles);

  const canViewCandidates = isPermissionPresent(
    K.Permissions.ViewCandidates,
    userRoles,
  );
  const candidatesSlice = useSelector(selectCandidates);
  const candidates = useSelector(selectCandidates);

  const { clientId, classId, statusesId, subStatusesId } = useParams();

  const [settingsForm] = Form.useForm();
  const initialSettingsRef = useRef(null);

  const [search, setSearch] = usePageFilter(`class-${classId}-search`);

  const [candidateActivity, setCandidateActivity] = useState(null);
  const [fetchedJobRecruiters, setFetchedJobRecruiters] = useState(null);
  const [modalVisible, setModalVisible] = useState({
    isSettingModalVisible: false,
    isClassCancelModalVisible: false,
    isCancelBillingModalVisible: false,
  });

  const [classData, setClassData] = useState({
    classDetails: null,
    statistics: null,
    status: [],
    subStatus: {},
    settings: null,
    shouldUpdate: false,
    shouldUpdateListing: false,
    moveCandidates: false,
  });

  const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);

  const classStatus = {
    open: "open-flag",
    closed: "closed-flag",
    "on hold": "onhold-flag",
    custom: "blue-flag",
  };

  const lookupTables = useSelector(selectConfigration).lookup;

  const showSettingsModal = () => {
    settingsForm.setFieldsValue(initialSettingsRef.current);
    setModalVisible((prev) => {
      return { ...prev, isSettingModalVisible: true };
    });
  };

  const showAssigCandidateModal = () => {
    setIsAssignModalVisible(true);
  };

  const handleOkSetting = () => {
    settingsForm.submit();
  };

  const handleCancelSetting = () => {
    settingsForm.resetFields();
    setModalVisible((prev) => {
      return { ...prev, isSettingModalVisible: false };
    });
  };

  const getStatusBreakdown = async () => {
    try {
      const res = await Class.getSubStatusBreakdown(classId);
      const subStatus = groupBy(res, (item) => item.status.name);
      const status = map(subStatus, (item) => {
        const total = item.reduce(
          (prev, curr) => curr.candidatesToSubStatusCount + prev,
          0,
        );
        return { id: item[0].status.id, name: item[0].status.name, total };
      });

      setClassData((prev) => ({ ...prev, status, subStatus }));
    } catch (err) {
      console.error(err);
    }
  };

  const getClassStatistics = async () => {
    try {
      const res = await Class.getStatistics(classId);
      setClassData((prev) => ({ ...prev, statistics: res }));
    } catch (err) {
      console.error(err);
    }
  };

  const getClassSettings = async () => {
    try {
      const res = await Class.getSettings(classId);
      setClassData((prev) => ({ ...prev, settings: res }));
    } catch (err) {
      console.error(err);
    }
  };

  const getClassDetails = async () => {
    try {
      const res = await Class.getClassDetailById(classId);

      setClassData((prev) => ({
        ...prev,
        classDetails: {
          ...res,
          vacancies: res.classVacancies.at(-1),
        },
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const onStatusChange = async (statusId) => {
    if (statusId === undefined) {
      return null;
    }
    const body = {
      classId,
      classStatusId: statusId,
    };
    setClassData((prev) => ({
      ...prev,
      classDetails: { ...prev.classDetails, classStatusId: statusId },
    }));

    try {
      await Class.updateClassStatus(body);
      message.success("Class Status Updated Successfully.");
    } catch (err) {
      console.error(err);
      message.error("Failed to Updated Class Status.");
    }
  };

  const getCandidateUdfs = async (entityKeyId = null, loading = true) => {
    const candidateUdfs = await UDF.getEditUdfFields(
      {
        clientId,
        entityId: K.UDF.Entities.Candidate.id,
        entityKey: K.UDF.Entities.Candidate.key,
        entityKeyId,
        isVisible: 1,
      },
      loading,
    );

    dispatch(setCandidateUdf(candidateUdfs));
    return candidateUdfs;
  };

  const getClassStatus = async () => {
    try {
      await dispatch(
        LookupTable.getData(K.Redux.ClassStatus, Lookup.ClassStatus),
      );
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getClassStatus();
  }, [clientId, classId]);

  useEffect(() => {
    const jobSourceId = candidateActivity?.jobSource?.id;

    if (!jobSourceId) {
      return undefined;
    }

    let isCancelled = false;

    Candidate.getJobRecruiterByJobId(jobSourceId, false, {
      showError: false,
      skipForbiddenRedirect: true,
    })
      .then((items) => {
        if (!isCancelled) setFetchedJobRecruiters({ jobSourceId, items });
      })
      .catch((err) => {
        if (!isCancelled) setFetchedJobRecruiters({ jobSourceId, items: [] });
        console.error(err);
      });

    return () => {
      isCancelled = true;
    };
  }, [candidateActivity?.jobSource?.id]);

  const jobRecruiters =
    fetchedJobRecruiters &&
    fetchedJobRecruiters.jobSourceId === candidateActivity?.jobSource?.id
      ? fetchedJobRecruiters.items
      : null;

  useEffect(() => {
    (async () => {
      await Promise.all([
        getClassDetails(),
        getClassSettings(),
        getClassStatistics(),
        getStatusBreakdown(),
        getCandidateUdfs(),
      ]);
    })();
  }, [
    classId,
    clientId,
    classData.shouldUpdate,
    classData.shouldUpdateListing,
    candidatesSlice.shouldUpdate,
  ]);

  const getClassStatusName = (classStatusId) => {
    return (
      lookupTables.classStatus?.find((item) => item.id === classStatusId)
        ?.name ?? K.NullPlaceholder
    );
  };
  const getClassStatusColor = (classStatusId) => {
    const status = getClassStatusName(classStatusId).toLowerCase();
    const statusColor = ["open", "closed", "on hold"].includes(status)
      ? status
      : "custom";

    return statusColor;
  };

  const showModalViewActivity = () => {
    setModalVisible((prev) => {
      return {
        ...prev,
        isActivityModalVisible: true,
      };
    });
  };

  const fetchCandidateDetails = async (id) => {
    try {
      const res = await Candidate.getActivity(id);
      setCandidateActivity(res);
      dispatch(setShouldReinitialize());
      showModalViewActivity();
    } catch (err) {
      message.error("Failed to fetch candidate!");
      console.error(err);
    }
  };

  const onCellClicked = (event) => {
    event.api.deselectAll();
    event.node.setSelected(true);
    fetchCandidateDetails(event.data.id);
  };

  const getCandidateByClassId = async () => {
    try {
      const data = await Candidate.getByClassId(classId);
      dispatch(setCandidateList(data.candidateJobEntries));
    } catch (err) {
      console.error(err);
    }
  };

  const updateData = () => {
    setClassData((prev) => {
      return { ...prev, shouldUpdateListing: !prev.shouldUpdateListing };
    });
  };

  const onStatusChanged = (updatedCandidate, declinedCandidates = []) => {
    dispatch(bulkEditCandidateList([updatedCandidate, ...declinedCandidates]));
  };

  const onCandidateDetached = () => {
    setCandidateActivity(null);
    updateData();
  };

  const onCandidateUpdated = (candidate) => {
    setCandidateActivity(candidate);
    updateData();
  };

  useEffect(() => {
    getCandidateByClassId();
  }, [classData.shouldUpdateListing, statusesId, subStatusesId]);

  const isSettingsModalDisabled = !isPermissionPresent(
    K.Permissions.EditClass,
    userRoles,
  )
    ? true
    : !classData.classDetails?.vacancies?.isActive ?? true;

  return (
    <>
      <div className={styles.wrapPageHeader}>
        <PageHeader
          className={[
            LayoutCss.appPageHeader,
            `${LayoutCss.clientsJobPageHeader} client-page-head` +
              ` client-details`,
          ]}
          ghost={false}
          onBack={history.goBack}
          title={
            <>
              <span className="icon-view-wrap">
                <i className="icon-view-detail" />
              </span>
              <Text>{classData.classDetails?.title}</Text>
              <Text
                className={
                  classStatus[
                    getClassStatusColor(classData?.classDetails?.classStatusId)
                  ]
                }
              >
                <span className="dot" />
                {lookupTables.classStatus
                  ?.find(
                    (item) =>
                      item.id === classData?.classDetails?.classStatusId,
                  )
                  ?.name.toUpperCase()}
              </Text>
            </>
          }
          subTitle={classData.classDetails?.name}
        />
      </div>
      <ClassStatistics styles={styles} classData={classData} />

      <div className={`job-card-wrap ${styles.jobCardWrap}`}>
        {screens.xl ? (
          <PanelGroup
            direction="horizontal"
            autoSaveId="class-details-view-activity"
          >
            <Panel id="class-table" defaultSize={58} minSize={30}>
              <DataTableLayout
                className={
                  candidateActivity ? "border-radius-right-none" : undefined
                }
                searchDefaultValue={search}
                onSearchChange={setSearch}
                actions={[
                  {
                    renderer: () => (
                      <Col style={{ width: 200 }}>
                        <Select
                          showSearch
                          style={{ width: "100%" }}
                          placeholder="Class Status"
                          optionFilterProp="label"
                          value={classData?.classDetails?.classStatusId}
                          options={lookupTables?.classStatus.map((item) => ({
                            value: item.id,
                            label: item.name,
                          }))}
                          onChange={onStatusChange}
                          disabled={
                            !isPermissionPresent(
                              K.Permissions.EditClass,
                              userRoles,
                            )
                          }
                        />
                      </Col>
                    ),
                  },
                  {
                    permissions: [K.Permissions.CreateCandidate],
                    renderer: () => (
                      <Col>
                        <AddClassCandidateButton
                          disabled={
                            !classData.classDetails?.vacancies?.isActive ?? true
                          }
                          classId={classId}
                          getCandidateUdfs={getCandidateUdfs}
                          onSuccess={(candidate) =>
                            dispatch(updateCandidateList(candidate))
                          }
                        />
                      </Col>
                    ),
                  },
                  {
                    permissions: [K.Permissions.CreateCandidate],
                    renderer: () => (
                      <Col>
                        <Button
                          type="primary"
                          onClick={showAssigCandidateModal}
                          disabled={
                            !classData.classDetails?.vacancies?.isActive ?? true
                          }
                        >
                          Assign Candidate
                        </Button>
                      </Col>
                    ),
                  },
                  {
                    permissions: [K.Permissions.EditClass],
                    renderer: () => (
                      <Col>
                        <Button
                          onClick={showSettingsModal}
                          className={styles.bottomCardIcon}
                        >
                          <SettingFilled />
                        </Button>
                      </Col>
                    ),
                  },
                ]}
              >
                {canViewCandidates && (
                  <ClientSideDataGrid
                    userId={userId}
                    configKey={K.AgGridTable.Keys.ClassDetailsVisibleColumn}
                    rowData={candidates.listing}
                    defaultColumnConfig={getColumns()}
                    quickFilterText={search}
                    getContextMenuItems={() =>
                      AgGridUtility.menuItemsWithExport
                    }
                    rowSelection="single"
                    suppressRowClickSelection
                    onCellClicked={onCellClicked}
                    rowStyle={{ cursor: "pointer" }}
                  />
                )}
              </DataTableLayout>
            </Panel>
            {candidateActivity && jobRecruiters && (
              <>
                <PanelResizeDivider />
                <Panel id="class-activity" defaultSize={42} minSize={30}>
                  <ViewActivity
                    className="border-radius-left-none"
                    styles={styles}
                    clientId={clientId}
                    candidateActivity={candidateActivity}
                    setCandidateActivity={setCandidateActivity}
                    jobDetails={{
                      ...candidateActivity.jobSource,
                      jobRecruiters,
                    }}
                    showEngagementTablePreview
                    onCancel={() => setCandidateActivity(null)}
                    onStatusChanged={onStatusChanged}
                    onCandidateChanged={onCandidateUpdated}
                    renderHeaderActions={(props) => (
                      <Space size={0}>
                        {isPermissionPresent(
                          K.Permissions.EditCandidate,
                          userRoles,
                        ) && (
                          <DetachCandidateButton
                            candidateActivity={candidateActivity}
                            styles={props.styles}
                            classId={classId}
                            disabled={false}
                            onSuccess={onCandidateDetached}
                          />
                        )}
                      </Space>
                    )}
                  />
                </Panel>
              </>
            )}
          </PanelGroup>
        ) : (
          <Row gutter={[16, 16]}>
            <Col xs={24} xl={candidateActivity ? 14 : 24}>
              <DataTableLayout
                searchDefaultValue={search}
                onSearchChange={setSearch}
                actions={[
                  {
                    renderer: () => (
                      <Col style={{ width: 200 }}>
                        <Select
                          showSearch
                          style={{ width: "100%" }}
                          placeholder="Class Status"
                          optionFilterProp="label"
                          value={classData?.classDetails?.classStatusId}
                          options={lookupTables?.classStatus.map((item) => ({
                            value: item.id,
                            label: item.name,
                          }))}
                          onChange={onStatusChange}
                        />
                      </Col>
                    ),
                  },
                  {
                    permissions: [K.Permissions.CreateCandidate],
                    renderer: () => (
                      <Col>
                        <AddClassCandidateButton
                          disabled={
                            !classData.classDetails?.vacancies?.isActive ?? true
                          }
                          classId={classId}
                          getCandidateUdfs={getCandidateUdfs}
                          onSuccess={(candidate) =>
                            dispatch(updateCandidateList(candidate))
                          }
                        />
                      </Col>
                    ),
                  },
                  {
                    permissions: [K.Permissions.CreateCandidate],
                    renderer: () => (
                      <Col>
                        <Button
                          type="primary"
                          onClick={showAssigCandidateModal}
                          disabled={
                            !classData.classDetails?.vacancies?.isActive ?? true
                          }
                        >
                          Assign Candidate
                        </Button>
                      </Col>
                    ),
                  },
                  {
                    permissions: [K.Permissions.EditClass],
                    renderer: () => (
                      <Col>
                        <Button
                          onClick={showSettingsModal}
                          className={styles.bottomCardIcon}
                        >
                          <SettingFilled />
                        </Button>
                      </Col>
                    ),
                  },
                ]}
              >
                {canViewCandidates && (
                  <ClientSideDataGrid
                    userId={userId}
                    configKey={K.AgGridTable.Keys.ClassDetailsVisibleColumn}
                    rowData={candidates.listing}
                    defaultColumnConfig={getColumns()}
                    quickFilterText={search}
                    getContextMenuItems={() =>
                      AgGridUtility.menuItemsWithExport
                    }
                    rowSelection="single"
                    suppressRowClickSelection
                    onCellClicked={onCellClicked}
                    rowStyle={{ cursor: "pointer" }}
                  />
                )}
              </DataTableLayout>
            </Col>

            {candidateActivity && jobRecruiters && (
              <Col xs={24} xl={10}>
                <ViewActivity
                  styles={styles}
                  clientId={clientId}
                  candidateActivity={candidateActivity}
                  setCandidateActivity={setCandidateActivity}
                  jobDetails={{ ...candidateActivity.jobSource, jobRecruiters }}
                  showEngagementTablePreview
                  onCancel={() => setCandidateActivity(null)}
                  onStatusChanged={onStatusChanged}
                  onCandidateChanged={onCandidateUpdated}
                  renderHeaderActions={(props) => (
                    <Space size={0}>
                      {isPermissionPresent(
                        K.Permissions.EditCandidate,
                        userRoles,
                      ) && (
                        <DetachCandidateButton
                          candidateActivity={candidateActivity}
                          styles={props.styles}
                          classId={classId}
                          disabled={false}
                          onSuccess={onCandidateDetached}
                        />
                      )}
                    </Space>
                  )}
                />
              </Col>
            )}
          </Row>
        )}
      </div>

      <Modal
        centered
        open={modalVisible.isSettingModalVisible}
        onCancel={handleCancelSetting}
        width={572}
        destroyOnClose
        closeIcon={<i className="icon-closeable" />}
        title="Class Settings"
        className="s2-theme-style modal-overflow-auto"
        footer={
          <>
            <Button
              className={styles.jobCancelButton}
              onClick={handleCancelSetting}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleOkSetting}
              className={styles.jobConfirmButton}
              disabled={isSettingsModalDisabled}
            >
              Save
            </Button>
          </>
        }
      >
        <ClassSettingModal
          classId={classId}
          classData={classData}
          form={settingsForm}
          setClassData={setClassData}
          classDetails={classData.classDetails}
          classStats={classData.settings}
          setModalVisible={setModalVisible}
          isDisabled={isSettingsModalDisabled}
        />
      </Modal>

      <AssignCandidate
        isModalVisible={isAssignModalVisible}
        setIsModalVisible={setIsAssignModalVisible}
        clientId={clientId}
        classId={classId}
        setClassData={setClassData}
        classCandidates={candidatesSlice.listing.map((item) => item.id)}
      />
    </>
  );
}
